import { Pcg32, STREAM_CAMPAIGN, seedToBigInt, type CardSpec, type FinishId } from '../../engine';
import { CampaignError } from '../campaign';
import type { Profile } from '../types';
import {
  CARD_PRICE,
  CARVING_INFO,
  CURIO_PRICE,
  APPRAISAL_PRICE,
  FINISH_PRICE,
  LONG_NIGHT,
  STAMP_PRICE,
  SUPERSTITION_INFO,
  TRICK_PRICE,
  type FixtureId,
} from './catalog';
import type { LedgerProfile, NightRun, Offer } from './types';

/** Helpers shared by the Long Night's run, Counter and Detours. */

/**
 * A random stream for one purpose and place in the run, derived from the run seed: ringing the
 * bell or opening Lots in another order never changes the next table (docs/SHOP_DESIGN.md §17.4).
 */
export const rngFor = (run: Pick<NightRun, 'seed'>, key: string): Pcg32 =>
  Pcg32.seeded(seedToBigInt(`${run.seed}|${key}`), STREAM_CAMPAIGN);

export function weightedPick<T>(rng: Pcg32, entries: readonly (readonly [T, number])[]): T {
  const total = entries.reduce((n, [, w]) => n + Math.max(0, w), 0);
  if (total <= 0) throw new CampaignError('nothing to choose from');
  let r = rng.nextBelow(total);
  for (const [v, w] of entries) {
    if (w <= 0) continue;
    if (r < w) return v;
    r -= w;
  }
  return entries[entries.length - 1]![0];
}

export function nextUid(run: NightRun, prefix = 'i'): string {
  run.uid += 1;
  return `${prefix}${run.uid}`;
}

export const owns = (run: Pick<NightRun, 'fixtures'>, id: FixtureId): boolean =>
  run.fixtures.includes(id);

/** The Long Night profile; throws when it does not exist yet. */
export function ledgerOf(p: Profile): LedgerProfile {
  if (!p.ledger) throw new CampaignError('the Long Night has not started');
  return p.ledger;
}

export function nightRunOf(p: Profile): NightRun {
  const run = p.ledger?.run;
  if (!run) throw new CampaignError('no Long Night run is in progress');
  return run;
}

/** Mantel slots: 5, plus the Altar Shelf, The Broker's Hand and the IOU Pledge. */
export const mantelSlots = (run: NightRun): number =>
  LONG_NIGHT.MANTEL_SLOTS + run.extraSlots - (run.pledge === 'dice' ? 1 : 0);

/** Mantel slots in use: a Ghost Superstition takes none. */
export const mantelUsed = (run: NightRun): number =>
  run.mantel.filter((i) => i.finish !== 'ghost').length;

export const curioSlots = (run: NightRun): number =>
  LONG_NIGHT.CURIO_SLOTS + (owns(run, 'coat-hook') ? 1 : 0);

/** The base price of an offer, before Scales, Coupons and debt. */
export function basePrice(offer: Offer): number {
  switch (offer.kind) {
    case 'superstition': {
      const info = SUPERSTITION_INFO[offer.id as keyof typeof SUPERSTITION_INFO];
      return (info?.price ?? 5) + (offer.finish ? FINISH_PRICE[offer.finish] : 0);
    }
    case 'curio':
      return CURIO_PRICE[offer.id] ?? 3;
    case 'appraisal':
      return APPRAISAL_PRICE;
    case 'card':
      return cardPrice(offer.card);
    case 'carving':
      return CARVING_INFO[offer.id as keyof typeof CARVING_INFO]?.price ?? 6;
    case 'bargain':
    case 'route':
    case 'omen':
      return 4;
  }
}

export const cardPrice = (card: CardSpec): number =>
  CARD_PRICE +
  (card.stamp ? STAMP_PRICE : 0) +
  (card.finish ? FINISH_PRICE[card.finish] : 0) +
  (card.trick ? TRICK_PRICE : 0);

/** What something costs at this Counter: Scales take a share off, debt adds ¤1, a Coupon is free. */
export function priceAt(run: NightRun, base: number, coupon = false): number {
  if (coupon) return 0;
  let price = base;
  if (owns(run, 'gold-scale')) price = Math.floor(price * 0.5);
  else if (owns(run, 'brass-scale')) price = Math.floor(price * 0.75);
  if (run.brass < 0) price += 1;
  return Math.max(base > 0 ? 1 : 0, price);
}

/** What selling an item brings: half its price (at least ¤1), plus a Piggy Bank's growth. */
export function sellValue(base: number, extra = 0): number {
  return Math.max(1, Math.floor(base / 2)) + extra;
}

/** How far into debt the run may go (Ledger, Black Ledger, the IOU Pledge). */
export const debtLimit = (run: NightRun): number =>
  owns(run, 'black-ledger') ? 25 : owns(run, 'ledger') || run.pledge === 'iou' ? 10 : 0;

/** Pays brass, or throws if the run cannot afford it. */
export function pay(run: NightRun, amount: number): void {
  if (amount <= 0) return;
  if (run.brass - amount < -debtLimit(run)) throw new CampaignError('not enough brass');
  run.brass -= amount;
}

export const finishOf = (id: string): FinishId | undefined =>
  id === 'foil' || id === 'gilt' || id === 'pearl' || id === 'ghost' ? id : undefined;
